import {
  Component,
  ElementRef,
  NgZone,
  OnDestroy,
  ViewChild,
  effect,
  input,
  signal,
  afterNextRender,
} from '@angular/core';
import * as am4charts from '@amcharts/amcharts4/charts';
import * as am4core from '@amcharts/amcharts4/core';
import { TranslateService } from '@ngx-translate/core';
import { DiscoveredUrlsByDepthData } from '../../models/discoveredUrls';

type ChartData = { depth: string; count: number };

@Component({
  selector: 'app-graph-crawler-discovery-depth-repartition',
  templateUrl: './graph-crawler-discovery-depth-repartition.component.html',
  styleUrls: ['./graph-crawler-discovery-depth-repartition.component.scss'],
})
export class GraphCrawlerDiscoveryDepthRepartitionComponent implements OnDestroy {
  readonly data = input<DiscoveredUrlsByDepthData[]>([]);
  readonly loading = input(true);
  readonly title = input(true);
  readonly height = input(300);
  readonly error = input(false);
  readonly noData = input(false);

  @ViewChild('graphclassdiv') chartElement?: ElementRef<HTMLElement>;

  private chart?: am4charts.PieChart;

  /** Flips true after the component's first render, so the effect below
   *  never races ahead of the @if-gated div in the template. */
  private readonly viewReady = signal(false);

  constructor(
    private readonly zone: NgZone,
    private readonly translate: TranslateService,
  ) {
    afterNextRender(() => this.viewReady.set(true));

    effect(() => {
      const data: ChartData[] = [...this.data()].map(({ depth, count }) => ({
        depth: `${this.translate.instant('INTEGRATIONS.BACKLINK_CRAWLER.DEPTH')}: ${depth}`,
        count,
      }));
      const canShow = this.viewReady() && !this.loading() && !this.error() && !this.noData();

      if (canShow) {
        this.renderChart(data);
      }
    });
  }

  ngOnDestroy(): void {
    this.chart?.dispose();
  }

  /** Retries a beat later if the @if hasn't re-inserted the div into the
   *  DOM yet */
  private renderChart(data: ChartData[]): void {
    if (!this.chartElement) {
      queueMicrotask(() => this.renderChart(data));
      return;
    }

    this.zone.runOutsideAngular(() => {
      if (!this.chart) {
        this.chart = this.createChart(this.chartElement!.nativeElement);
      }
      this.chart.data = [...data].sort((a, b) => b.count - a.count);
    });
  }

  private createChart(element: HTMLElement): am4charts.PieChart {
    const chart = am4core.create(element, am4charts.PieChart);
    chart.logo.setVisibility(false);
    chart.radius = am4core.percent(65);

    const pieSeries = chart.series.push(new am4charts.PieSeries());
    pieSeries.showOnInit = false;
    pieSeries.dataFields.value = 'count';
    pieSeries.dataFields.category = 'depth';
    pieSeries.slices.template.stroke = am4core.color('#fff');
    pieSeries.slices.template.strokeOpacity = 1;
    pieSeries.labels.template.fill = am4core.color('#747479');
    pieSeries.labels.template.fontSize = 10;
    pieSeries.labels.template.fontWeight = 'bold';
    pieSeries.labels.template.fontFamily = 'Source Sans 3, sans-serif';
    pieSeries.labels.template.text = '{category}';
    pieSeries.labels.template.wrap = true;
    pieSeries.labels.template.maxWidth = 100;
    pieSeries.alignLabels = false;

    const hideSmall = (ev: any) => {
      const show = ev.target.dataItem && ev.target.dataItem.values.value.percent >= 5;
      show ? ev.target.show() : ev.target.hide();
    };
    pieSeries.ticks.template.events.on('ready', hideSmall);
    pieSeries.ticks.template.events.on('visibilitychanged', hideSmall);
    pieSeries.labels.template.events.on('ready', hideSmall);
    pieSeries.labels.template.events.on('visibilitychanged', hideSmall);

    // Initial "grow from center" animation
    pieSeries.hiddenState.properties.opacity = 1;
    pieSeries.hiddenState.properties.endAngle = -90;
    pieSeries.hiddenState.properties.startAngle = -90;
    chart.hiddenState.properties.radius = am4core.percent(0);

    return chart;
  }
}
